import { FolderOpen, ListTodo, NotebookPen, Search, Users } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { RoomContextPanel } from "./room-context"
import { RoomFilesPanel } from "./room-files"
import { RoomSearchPanel } from "./room-search"
import { RoomTasksPanel } from "./room-tasks"
import { RoomWorkersPanel } from "./room-workers"

export const PANEL_TABS = ["tasks", "plan", "team", "files", "search"] as const
export type PanelTab = (typeof PANEL_TABS)[number]

export function isPanelTab(v: unknown): v is PanelTab {
  return typeof v === "string" && (PANEL_TABS as readonly string[]).includes(v)
}

const ICONS = { tasks: ListTodo, plan: NotebookPen, team: Users, files: FolderOpen, search: Search } as const

/** Room side panel: tasks, plan & context, roster, files and search. */
export function RoomSidePanel({
  room,
  tab,
  onTabChange,
}: {
  room: RoomDetail
  tab: PanelTab
  onTabChange: (tab: PanelTab) => void
}) {
  const { t } = useTranslation()
  const open = room.tasks.filter((x) => x.status !== "done").length
  return (
    <Tabs
      value={tab}
      onValueChange={(v) => isPanelTab(v) && onTabChange(v)}
      className="flex h-full min-h-0 flex-col gap-0"
    >
      <div className="shrink-0 overflow-x-auto border-b px-2">
        <TabsList variant="line" className="h-11 w-full justify-start">
          {PANEL_TABS.map((id) => {
            const Icon = ICONS[id]
            return (
              <TabsTrigger
                key={id}
                value={id}
                className="flex-none px-2 text-xs"
                data-testid={`room-tab-${id}`}
              >
                <Icon className="size-3.5" />
                {t(`workplace.room.tabs.${id}`)}
                {id === "tasks" && open > 0 && (
                  <span className="bg-primary/10 text-primary rounded-full px-1.5 text-[0.625rem] tabular-nums">
                    {open}
                  </span>
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <TabsContent value="tasks">
          <RoomTasksPanel tasks={room.tasks} />
        </TabsContent>
        <TabsContent value="plan">
          <RoomContextPanel room={room} />
        </TabsContent>
        <TabsContent value="team">
          <RoomWorkersPanel room={room} />
        </TabsContent>
        <TabsContent value="files">
          <RoomFilesPanel roomId={room.id} />
        </TabsContent>
        <TabsContent value="search">
          <RoomSearchPanel room={room} onOpenFiles={() => onTabChange("files")} />
        </TabsContent>
      </div>
    </Tabs>
  )
}
